<template>
  <div class="galeria-table" data-testid="galeria-table">
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :data="rowData"
      :autoLoad="false"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      data-testid="galeria-data-table"
      @refresh="loadGaleriaData"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGaleria } from '@/composables/useGaleria';
import { useFiltro } from '@/composables/useFiltro';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import type { Galeria_ImageRow } from '@/contracts/Galeria_ImageRow';

interface CellRendererParams {
  value: string | undefined;
  data: Galeria_ImageRow;
}

const { t } = useI18n();

const emit = defineEmits<{
  (e: 'view-gallery', payload: { compromissoServicoId: string | null; imageType: string | null }): void;
}>();

const { fetchGaleriaImages } = useGaleria();
useFiltro();

const datatableRef = ref<unknown>(null);
const rowData = ref<Galeria_ImageRow[]>([]);

const datatableConfig = computed(() => {
  return {
    tableId: 'tabelaGaleria',
    title: t('Imagens dos serviços'),
    filename: `galeria_${new Date().toISOString().split('T')[0]}`,
    rowTestId: (data: Record<string, unknown>): string => {
      const id = data.compromisso_servico_id;
      return id == null ? '' : `galeria-row-${id}`;
    },
    pagination: true,
    pageLength: 25,
    stripedRows: true,
    showGridlines: true,
    resizableColumns: true,
    sortable: true,
    showFilters: true,
    size: 'small',
    columns: [
      {
        data: 'operador',
        title: t('Operador'),
        width: 120,
        sortable: true,
        filterType: 'text'
      },
      {
        data: 'franquia',
        title: t('Franquia'),
        width: 120,
        sortable: true,
        filterType: 'text'
      },
      {
        data: 'cliente_nome',
        title: t('Cliente'),
        width: 180,
        sortable: true,
        filterType: 'text'
      },
      {
        data: 'bairro',
        title: t('Bairro'),
        width: 150,
        sortable: true,
        filterType: 'text'
      },
      {
        data: 'valor',
        title: t('Valor'),
        width: 100,
        sortable: true,
        renderType: 'currency',
        dataType: 'numeric'
      },
      {
        data: 'inicio',
        title: t('Início'),
        width: 140,
        sortable: true,
        filterType: 'text'
      },
      {
        data: 'fim',
        title: t('Fim'),
        width: 140,
        sortable: true,
        filterType: 'text'
      },
      {
        data: 'status',
        title: t('Status'),
        width: 110,
        sortable: true,
        filterType: 'select'
      },
      {
        data: 'servico',
        title: t('Serviço'),
        width: 200,
        sortable: true,
        // options derived from the loaded rows (as the status column does)
        filterType: 'select'
      },
      {
        data: 'antes_thumbnail_url',
        title: t('Antes'),
        width: 100,
        sortable: false,
        cellRenderer: (params: CellRendererParams): string => {
          const thumbnailUrl = params.value;
          const fullUrl = params.data.antes_url;

          if (!thumbnailUrl && !fullUrl) return '-';

          const displayUrl = thumbnailUrl || fullUrl;

          return `
            <div class="image-cell">
              <img
                src="${displayUrl}"
                alt="${t('Antes')}"
                class="thumbnail-image"
                data-testid="galeria-thumbnail-antes"
                data-full-url="${fullUrl || displayUrl}"
                data-type="antes"
                data-row-id="${params.data.compromisso_servico_id}"
                loading="lazy"
                onerror="this.style.display='none'"
                title="${t('Clique para ampliar')}"
              />
            </div>
          `;
        }
      },
      {
        data: 'depois_thumbnail_url',
        title: t('Depois'),
        width: 100,
        sortable: false,
        cellRenderer: (params: CellRendererParams): string => {
          const thumbnailUrl = params.value;
          const fullUrl = params.data.depois_url;

          if (!thumbnailUrl && !fullUrl) return '-';

          const displayUrl = thumbnailUrl || fullUrl;

          return `
            <div class="image-cell">
              <img
                src="${displayUrl}"
                alt="${t('Depois')}"
                class="thumbnail-image"
                data-testid="galeria-thumbnail-depois"
                data-full-url="${fullUrl || displayUrl}"
                data-type="depois"
                data-row-id="${params.data.compromisso_servico_id}"
                loading="lazy"
                onerror="this.style.display='none'"
                title="${t('Clique para ampliar')}"
              />
            </div>
          `;
        }
      }
    ]
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Exportar CSV')
  };
});

const loadGaleriaData = async (): Promise<void> => {
  log('GaleriaTable', 'loadGaleriaData', 'Loading galeria images using useGaleria composable');
  try {
    const data = await fetchGaleriaImages();
    log('GaleriaTable', 'loadGaleriaData', 'Data loaded successfully', data.length, 'rows');
    rowData.value = data;
  } catch (error) {
    log('GaleriaTable', 'loadGaleriaData', 'Error loading data', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', `${t('Erro ao carregar imagens')}: ${message}`);
  }
};

const handleImageClick = (event: Event): void => {
  const target = event.target as HTMLElement | null;
  const img = target?.closest('.thumbnail-image') as HTMLElement | null;
  if (!img) return;

  event.preventDefault();

  const rowId = img.getAttribute('data-row-id');
  const imageType = img.getAttribute('data-type');

  log('GaleriaTable', 'handleImageClick', 'Image clicked', { rowId, imageType });

  emit('view-gallery', {
    compromissoServicoId: rowId,
    imageType
  });
};

const refreshData = (): void => {
  log('GaleriaTable', 'refreshData', 'Manually refreshing data');
  loadGaleriaData();
};

onMounted(() => {
  log('GaleriaTable', 'onMounted', 'Component mounted');

  log('GaleriaTable', 'onMounted', 'Loading initial data');
  loadGaleriaData();

  eventBus.on('filter.updated', refreshData);
  eventBus.on('galeria.refresh', refreshData);

  document.addEventListener('click', handleImageClick);
});

onUnmounted(() => {
  log('GaleriaTable', 'onUnmounted', 'Component unmounted');
  eventBus.off('filter.updated', refreshData);
  eventBus.off('galeria.refresh', refreshData);
  document.removeEventListener('click', handleImageClick);
});
</script>

<style scoped>
.galeria-table {
  width: 100%;
}

:deep(.image-cell) {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 5px;
}

:deep(.thumbnail-image) {
  max-height: 80px;
  max-width: 100%;
  height: auto;
  width: auto;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  object-fit: cover;
}

:deep(.thumbnail-image:hover) {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
</style>
